import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Power, Trash2, X } from "lucide-react";
import api, { errMsg, inr } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { useAuth } from "../context/AuthContext";

const empty = { productCode: "", itemName: "", stdNonStd: "Std", colour: "", category: "", defaultUnitRate: 0 };

export default function Products() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const perms = user.permissions || [];
  const canAdd = isAdmin || perms.includes("add_product");
  const canEdit = isAdmin || perms.includes("edit_product");
  const canDelete = isAdmin || perms.includes("delete_product");
  const canManage = canAdd || canEdit || canDelete;
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/products", { params: { search } }); setRows(data); };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  const openNew = () => { setForm(empty); setModal("new"); };
  const openEdit = (p) => { setForm(p); setModal(p.id); };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.productCode || !form.itemName) return toast.error("Product code and item name are required.");
    try {
      if (modal === "new") await api.post("/products", form);
      else await api.put(`/products/${modal}`, form);
      toast.success("Saved"); setModal(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (p) => {
    try { await api.patch(`/products/${p.id}/status`, null, { params: { status: p.status === "active" ? "inactive" : "active" } }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete product "${p.productCode}"?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Product Master" subtitle={`${rows.length} product(s)`}
        actions={canAdd && <button className="btn-accent" onClick={openNew} data-testid="add-product-btn"><Plus className="h-4 w-4" /> Add Product</button>} />
      <div className="p-6 lg:p-8 space-y-4">
        <input className="input max-w-sm" placeholder="Search products…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="product-search" />
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200"><tr>
              <th className="th">Code</th><th className="th">Item</th><th className="th">Std/Non Std</th><th className="th">Colour</th><th className="th">Category</th><th className="th">Unit Rate</th><th className="th">Status</th>{canManage && <th className="th">Actions</th>}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && <tr><td className="td text-slate-400" colSpan={8}>No products.</td></tr>}
              {rows.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`product-row-${p.id}`}>
                  <td className="td font-mono font-semibold text-slate-900">{p.productCode}</td>
                  <td className="td">{p.itemName}</td>
                  <td className="td">{p.stdNonStd}</td>
                  <td className="td">{p.colour || "—"}</td>
                  <td className="td">{p.category || "—"}</td>
                  <td className="td font-semibold">{inr(p.defaultUnitRate)}</td>
                  <td className="td"><StatusBadge status={p.status} /></td>
                  {canManage && <td className="td"><div className="flex gap-1">
                    {canEdit && <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4 text-slate-600" /></button>}
                    {canEdit && <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => toggle(p)} data-testid={`toggle-product-${p.id}`}><Power className={`h-4 w-4 ${p.status === "active" ? "text-green-600" : "text-slate-400"}`} /></button>}
                    {canDelete && <button className="p-1.5 rounded hover:bg-red-50" onClick={() => del(p)} data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4 text-red-600" /></button>}
                  </div></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="product-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head text-xl font-bold">{modal === "new" ? "Add Product" : "Edit Product"}</h3>
              <button onClick={() => setModal(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="label">Product Code *</label><input className="input" value={form.productCode} onChange={(e) => set("productCode", e.target.value)} data-testid="product-code-input" /></div>
              <div><label className="label">Item / Product Name *</label><input className="input" value={form.itemName} onChange={(e) => set("itemName", e.target.value)} data-testid="product-item-input" /></div>
              <div><label className="label">Std / Non Std</label><select className="input" value={form.stdNonStd} onChange={(e) => set("stdNonStd", e.target.value)}><option>Std</option><option>Non Std</option></select></div>
              <div><label className="label">Colour</label><input className="input" value={form.colour} onChange={(e) => set("colour", e.target.value)} /></div>
              <div><label className="label">Category</label><input className="input" value={form.category || ""} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Safe Locker, Coffer" data-testid="product-category-input" /></div>
              <div><label className="label">Default Unit Rate</label><input type="number" className="input" value={form.defaultUnitRate} onChange={(e) => set("defaultUnitRate", Number(e.target.value))} data-testid="product-rate-input" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button className="btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={save} data-testid="save-product-btn">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
